import PropTypes from 'prop-types';
import _ from 'underscore';

import {
  MIN_TOTAL_HEIGHT,
  PADDING_TOP,
  SCROLLBAR_WIDTH,
  STAGE_RIGHT_PADDING,
  STAGE_RUN_LEFT_PADDING,
  STAGE_RUN_RIGHT_PADDING,
  STAGE_WIDTH,
} from './GraphLayoutConstants';
import {TaggedNodeGraph} from './TaggedNodeGraph';

export const POSITION_SHAPE = PropTypes.shape({
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
});

export function getSum(arr: Array<number>): number {
  return arr.reduce((accumulator, value) => accumulator + value, 0);
}

/**
 * Class for visualizing a TaggedNodeGraph, by providing the position of each node
 * and the positions of all links between nodes.
 */
export class GraphLayout {
  graph: TaggedNodeGraph;
  stageRunRadius: number;
  verticalSpacing: number;

  constructor(taggedNodeGraph: TaggedNodeGraph, stageRunRadius: number) {
    this.graph = taggedNodeGraph;
    this.stageRunRadius = stageRunRadius;
    this.verticalSpacing = 3 * stageRunRadius;

    // Memoized functions
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.getNodePosition = _.memoize(this.getNodePosition);
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.getX = _.memoize(this.getX);
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.getY = _.memoize(this.getY);
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.getSubtreeHeight = _.memoize(this.getSubtreeHeight);
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.getTreeHeight = _.memoize(this.getTreeHeight);
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.getRelativeX = _.memoize(this.getRelativeX);
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.getTagWidth = _.memoize(this.getTagWidth);
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.getLinks = _.memoize(this.getLinks);
  }

  getNodePosition(nodeId: string): {x: number; y: number} {
    return {
      x: this.getX(nodeId),
      y: this.getY(nodeId),
    };
  }

  getX(nodeId: string): number {
    const currentTag = this.graph.getTag(nodeId);
    const previousTagWidths = _.range(currentTag).map((tag) => this.getTagWidth(tag));
    return getSum(previousTagWidths) + this.getRelativeX(nodeId);
  }

  getY(nodeId: string): number {
    const parentNodeId = this.graph.getPrimaryParentNodeId(nodeId);
    const parentY = parentNodeId ? this.getY(parentNodeId) : this.verticalSpacing;
    const siblingSubtreeHeights = this.graph
      .getSiblingsAboveNode(nodeId)
      .map((siblingId) => this.getSubtreeHeight(siblingId));
    return parentY + getSum(siblingSubtreeHeights);
  }

  /**
   * Get the height of the subtree containing the node and all its descendants,
   * including padding at the bottom.
   */
  getSubtreeHeight(nodeId: string): number {
    const childNodeIds = this.graph.getPrimaryChildNodeIds(nodeId);
    return childNodeIds.length
      ? getSum(childNodeIds.map((childNodeId) => this.getSubtreeHeight(childNodeId)))
      : this.verticalSpacing;
  }

  /**
   * Get the height of the entire tree visualization, including padding at the top.
   * (This is not the depth of the tree.)
   */
  getTreeHeight(): number {
    const initialSubtreeHeights = this.graph
      .getInitialNodeIds()
      .map((nodeId) => this.getSubtreeHeight(nodeId));
    return getSum(initialSubtreeHeights) + this.verticalSpacing;
  }

  /**
   * The X position of a node, relative to the left boundary of the current stage.
   */
  getRelativeX(nodeId: string): number {
    const currentTag = this.graph.getTag(nodeId);
    const parentNodeId = this.graph.getPrimaryParentNodeId(nodeId);
    if (parentNodeId) {
      const parentTag = this.graph.getTag(parentNodeId);
      if (parentTag === currentTag) {
        const parentRelativeX = this.getRelativeX(parentNodeId);
        return parentRelativeX + 2 * this.stageRunRadius + STAGE_RUN_LEFT_PADDING;
      }
    }
    return this.stageRunRadius + STAGE_RUN_LEFT_PADDING;
  }

  getTagWidth(tag: number): number {
    const relativeXs = this.graph.getNodeIdsByTag(tag).map((nodeId) => this.getRelativeX(nodeId));
    const aggNodesWidth = Math.max(...relativeXs) + this.stageRunRadius + STAGE_RUN_RIGHT_PADDING;
    return Math.max(STAGE_WIDTH, aggNodesWidth);
  }

  /**
   * Return a list of objects representing the information needed to render a link between nodes.
   * Include three kinds of links:
   *   - from the parent of a child to the child
   *   - from the left boundary to an initial node
   *   - from a terminal node to the right boundary
   */
  getLinks({leftBoundary, rightBoundary}: {leftBoundary: number; rightBoundary: number}): Array<{
    key: string;
    leftPosition: {x: number; y: number};
    rightPosition: {x: number; y: number};
    parentNodeId: string | null;
    childNodeId: string | null;
    isSecondary: boolean;
  }> {
    // Links from the left boundary to the initial nodes
    const initialLinks = this.graph.getInitialNodeIds().map((nodeId) => {
      const position = this.getNodePosition(nodeId);
      return {
        key: `beginning - ${nodeId}`,
        leftPosition: {x: leftBoundary, y: position.y},
        rightPosition: position,
        parentNodeId: null,
        childNodeId: nodeId,
        isSecondary: false,
      };
    });

    // eslint-disable-next-line no-restricted-syntax
    const nonInitialLinks = _.flatten(
      this.graph.getNodeIds().map((nodeId) => {
        const position = this.getNodePosition(nodeId);
        const childNodeIds = this.graph.getPrimaryChildNodeIds(nodeId);
        const secondaryChildNodeIds = this.graph.getSecondaryChildNodeIds(nodeId);
        if (childNodeIds.length > 0 || secondaryChildNodeIds.length > 0) {
          // Links from parent node to child nodes
          const primaryLinks = childNodeIds.map((childNodeId) => ({
            key: `${nodeId} - ${childNodeId}`,
            leftPosition: position,
            rightPosition: this.getNodePosition(childNodeId),
            parentNodeId: nodeId,
            childNodeId,
            isSecondary: false,
          }));
          const secondaryLinks = secondaryChildNodeIds.map((childNodeId) => ({
            key: `${nodeId} - ${childNodeId}`,
            leftPosition: position,
            rightPosition: this.getNodePosition(childNodeId),
            parentNodeId: nodeId,
            childNodeId,
            isSecondary: true,
          }));
          return [...primaryLinks, ...secondaryLinks];
        } else {
          // Link from terminal node to right boundary
          return [
            {
              key: `${nodeId} - end`,
              leftPosition: position,
              rightPosition: {x: rightBoundary, y: position.y},
              parentNodeId: nodeId,
              childNodeId: null,
              isSecondary: false,
            },
          ];
        }
      })
    );

    return [...initialLinks, ...nonInitialLinks];
  }

  /**
   * Return true if arms visualization can no longer be centered while maintaining the padding on top.
   */
  isTallVisualization(): boolean {
    return this.getTreeHeight() + 2 * PADDING_TOP > MIN_TOTAL_HEIGHT;
  }

  getTotalWidthIgnoringScrollbar(containerWidth: number): number {
    const tagWidths = this.graph.getTags().map((tag) => this.getTagWidth(tag));
    const totalTagWidth = getSum(tagWidths);
    return Math.max(containerWidth, totalTagWidth + STAGE_RIGHT_PADDING);
  }

  getTotalHeightIgnoringScrollbar(): number {
    return this.isTallVisualization()
      ? Math.max(MIN_TOTAL_HEIGHT, this.getTreeHeight() + PADDING_TOP)
      : MIN_TOTAL_HEIGHT;
  }

  overflowsHorizontally(containerWidth: number): boolean {
    return containerWidth < this.getTotalWidthIgnoringScrollbar(containerWidth);
  }

  overflowsVertically(): boolean {
    return MIN_TOTAL_HEIGHT < this.getTotalHeightIgnoringScrollbar();
  }

  /**
   * Get the total width of the visualization, where containerWidth is the width of the container.
   *
   * When the visualization overflows vertically, account for the vertical scrollbar
   * by making the width smaller so that it will not overflow horizontally because of the scrollbar.
   */
  getTotalWidth(containerWidth: number): number {
    const totalWidthIgnoringScrollbar = this.getTotalWidthIgnoringScrollbar(containerWidth);
    return this.overflowsVertically()
      ? totalWidthIgnoringScrollbar - SCROLLBAR_WIDTH
      : totalWidthIgnoringScrollbar;
  }

  /**
   * Get the total height of the visualization, where containerWidth is the width of the container.
   *
   * When the visualization overflows horizontally, account for the horizontal scrollbar
   * by making the height smaller so that it will not overflow vertically because of the scrollbar.
   */
  getTotalHeight(containerWidth: number): number {
    const totalHeightIgnoringScrollbar = this.getTotalHeightIgnoringScrollbar();
    return this.overflowsHorizontally(containerWidth)
      ? totalHeightIgnoringScrollbar - SCROLLBAR_WIDTH
      : totalHeightIgnoringScrollbar;
  }
}
